'use client';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';
import { groupMember, USER_GROUP } from '@/helpers/user';
import { useGetCommonStore } from '@/store';
import { groupMemberWithoutSilverVIP } from '../components/QuestPage';
import {
  determineMissionMode,
  MissionMode,
  MissionStatusGroups,
  type QuestMissionWithMode,
  questMissions,
} from '../helpers';

export const useQuestPromo = () => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const isVip = useMemo(() => groupMemberWithoutSilverVIP(user), [user]);
  const isBonusHunter = useMemo(
    () => groupMember(USER_GROUP.BONUS_HUNTERS)(user),
    [user]
  );
  const isVipOrBonusHunter = isVip || isBonusHunter;

  const missions: QuestMissionWithMode[] = useMemo(
    () =>
      questMissions.map((mission, index) => {
        const today = format(toZonedTime(new Date(), 'UTC'), 'yyyy-MM-dd');

        const isToday = mission.date === today;
        const isFuture = mission.date > today;

        const isComplete = !!user?.statuses?.find(({ id }) => {
          const subGroup = isVipOrBonusHunter
            ? MissionStatusGroups.VIP
            : MissionStatusGroups.Casual;

          return id === `${MissionStatusGroups.Daily}${index + 1}_${subGroup}`;
        });

        const missionStarted = isToday;

        const mode = determineMissionMode({
          isAuthenticated,
          isComplete,
          isToday,
          isFuture,
          missionStarted,
        });

        return { ...mission, mode };
      }),
    [user, isAuthenticated, isVipOrBonusHunter]
  );

  const { isTodayComplete, todayMissionIndex } = useMemo(() => {
    const todayMissionIndex = missions.findIndex(({ mode }) =>
      [
        MissionMode.TodayActive,
        MissionMode.TodayCompleted,
        MissionMode.TodayFailed,
      ].includes(mode)
    );

    const isTodayComplete =
      missions[todayMissionIndex]?.mode === MissionMode.TodayCompleted;

    return { isTodayComplete, todayMissionIndex };
  }, [missions]);

  return { missions, isTodayComplete, todayMissionIndex, isVipOrBonusHunter };
};
